// ============================================================
// HOOKED — CAST METER  (Phase 1 of the catch loop)
// Power oscillates on a sine wave. Space (or tap) locks it.
// Power decides the depth zone — and THE DEEP is where the
// real ones live. Overshooting past 96 whips the line: the
// cast lands, but the ring will close 15% faster (you spooked
// the water).
//
// v2 overhaul: the panel wears The Treatment — faceted brass
// frame, bevel, a charging-lantern power bar with a live glow
// head, shimmering zone bands, and a pulsing whip-danger sliver.
// Logic identical (sine sweep, whip>96, depth mapping).
// ============================================================

import { useEffect, useRef, useState } from "react";
import { GM, SD, WHARF_SLAB, facet, bevelPanel, textGlow, bloom } from "./theme";
import { DEPTHS, depthForPower } from "./FishManifest";

const OSC_MS = 1400; // full 0→100→0 sweep

export function CastMeter({ onCast }) {
  const [power, setPower] = useState(0);
  const rafRef = useRef(null);
  const t0Ref = useRef(performance.now());
  const powerRef = useRef(0);
  const lockedRef = useRef(false);

  useEffect(() => {
    const tick = (now) => {
      const t = (now - t0Ref.current) % OSC_MS;
      const p = Math.round((1 - Math.cos((t / OSC_MS) * Math.PI * 2)) * 50);
      powerRef.current = p;
      setPower(p);
      rafRef.current = requestAnimationFrame(tick);
    };
    rafRef.current = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(rafRef.current);
  }, []);

  const doCast = () => {
    if (lockedRef.current) return;
    lockedRef.current = true;
    cancelAnimationFrame(rafRef.current);
    const p = powerRef.current;
    onCast?.({ power: p, depth: depthForPower(p), whipped: p > 96 });
  };

  useEffect(() => {
    const onKey = (e) => {
      if (e.code === "Space" || e.key === "Enter") { e.preventDefault(); doCast(); }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [onCast]);

  const depth = depthForPower(power);
  const zoneColor =
    depth.id === "deep" ? GM.goldBright :
    depth.id === "mid"  ? SD.tealLight  : SD.starDim;

  return (
    <div
      onPointerDown={doCast}
      style={{
        position: "fixed", left: "50%", bottom: 90, transform: "translateX(-50%)",
        width: 320, zIndex: 300, cursor: "pointer", userSelect: "none",
        background: WHARF_SLAB, clipPath: facet(12),
        padding: "16px 20px 14px",
        ...bevelPanel(GM.gold),
        animation: "fadeUp 0.25s ease",
        fontFamily: GM.fontDisplay,
      }}
    >
      {/* brass rail header */}
      <div style={{
        display: "flex", alignItems: "center", justifyContent: "center", gap: 8,
        fontSize: 11, letterSpacing: 3, color: GM.goldBright, ...textGlow(GM.gold),
      }}>
        <RailTick /> ◆ THE CAST <RailTick />
      </div>

      {/* Power bar with zone bands */}
      <div style={{
        position: "relative", height: 26, marginTop: 12,
        background: `linear-gradient(180deg, ${GM.pageDeep}, ${GM.page})`,
        borderRadius: 7, overflow: "hidden",
        boxShadow: `inset 0 2px 4px rgba(0,0,0,0.6), inset 0 0 0 1px ${GM.inkBrown}`,
      }}>
        {/* zone bands */}
        {Object.values(DEPTHS).map(z => (
          <div key={z.id} style={{
            position: "absolute", top: 0, bottom: 0,
            left: `${z.min}%`, width: `${Math.min(z.max, 100) - z.min}%`,
            background: z.id === "deep" ? `${GM.gold}22` : z.id === "mid" ? `${SD.teal}22` : "transparent",
            borderLeft: z.min > 0 ? `1px solid ${GM.inkBrown}66` : "none",
          }} />
        ))}
        {/* whip warning sliver — pulses */}
        <div style={{
          position: "absolute", top: 0, bottom: 0, left: "96%", width: "4%",
          background: `${SD.redBright}44`,
          animation: "tensionPulse 0.7s ease-in-out infinite",
        }} />
        {/* fill — charging lantern */}
        <div style={{
          position: "absolute", top: 0, bottom: 0, left: 0,
          width: `${power}%`,
          background: `linear-gradient(90deg, ${SD.starDim}, ${zoneColor})`,
          boxShadow: `0 0 16px ${zoneColor}`,
          transition: "none",
        }} />
        {/* glowing head at the leading edge */}
        <div style={{
          position: "absolute", top: -2, bottom: -2, left: `calc(${power}% - 2px)`,
          width: 4, background: GM.goldPale,
          boxShadow: `0 0 14px ${GM.goldPale}, 0 0 6px #fff`,
          borderRadius: 2,
        }} />
      </div>

      <div style={{ display: "flex", justifyContent: "space-between", marginTop: 9, alignItems: "baseline" }}>
        <div style={{ fontSize: 10, letterSpacing: 1.5, color: zoneColor, ...textGlow(zoneColor) }}>{depth.label}</div>
        <div style={{ fontSize: 17, fontWeight: 900, color: GM.ink, fontFamily: GM.fontUI, textShadow: `0 0 10px ${zoneColor}66` }}>{power}</div>
      </div>
      <div style={{ fontSize: 9, color: GM.inkDim, letterSpacing: 1, textAlign: "center", marginTop: 7 }}>
        SPACE / TAP to loose the line · past 96 whips the water
      </div>
    </div>
  );
}

function RailTick() {
  return (
    <span style={{
      display: "inline-block", width: 28, height: 2, borderRadius: 2,
      background: `linear-gradient(90deg, transparent, ${GM.gold}, transparent)`,
      boxShadow: bloom(GM.gold),
    }} />
  );
}
